import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Form,
  Image,
  Alert,
  Spinner,
  Container,
  Row,
  Col,
  ProgressBar,
  Tab,
  Tabs,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import api from "../api";
import MathJaxRenderer from "./MathJaxRenderer";

// Add direct style to hide raw content elements
const rawContentHidingStyle = `
  /* Only hide specific raw content elements */
  .raw-content-container,
  pre:not(.markscheme-content),
  [data-rr-ui-event-key="raw"],
  #content-tabs-tabpane-raw,
  #markscheme-tabs-tabpane-raw {
    display: none !important;
  }
`;

// Component to inject the style
const RawContentHider = () => {
  useEffect(() => {
    // Keep track of elements that have been processed
    const processedElements = new WeakSet();

    // Flag to prevent concurrent processing
    let isProcessing = false;

    // Inject CSS
    const styleEl = document.createElement("style");
    styleEl.textContent = rawContentHidingStyle;
    document.head.appendChild(styleEl);

    // Custom JavaScript to hide raw content
    function hideRawContent() {
      // Prevent concurrent or nested executions
      if (isProcessing) return;
      isProcessing = true;

      console.log("Looking for elements to hide in RawContentHider");

      try {
        // Mark markscheme content to be preserved once
        const markschemeContainer = document.querySelector(
          '[id="markscheme-tabs-tabpane-rendered"]'
        );
        if (
          markschemeContainer &&
          !processedElements.has(markschemeContainer)
        ) {
          markschemeContainer.classList.add("markscheme-preview-container");
          processedElements.add(markschemeContainer);

          // Ensure the container is visible
          markschemeContainer.style.display = "block";

          // Make parent containers visible
          let parent = markschemeContainer.parentElement;
          while (parent) {
            if (
              parent.classList.contains("tab-content") ||
              parent.classList.contains("tab-pane")
            ) {
              parent.style.display = "block";
              processedElements.add(parent);
            }
            parent = parent.parentElement;
          }

          console.log("Preserved markscheme content");
        }

        // Hide tabs with "HTML Source" or "Raw" in their title, but NOT "Rendered" tabs
        document.querySelectorAll('[role="tab"]').forEach((tab) => {
          if (processedElements.has(tab)) return;

          if (
            tab.textContent &&
            (tab.textContent.includes("HTML Source") ||
              tab.textContent.includes("Raw Content") ||
              tab.textContent === "Raw")
          ) {
            tab.style.display = "none";
            processedElements.add(tab);
          }
        });

        // Find and hide elements with text "Raw content"
        document
          .querySelectorAll("h5, h6, p, div, span, label")
          .forEach((el) => {
            if (processedElements.has(el)) return;

            if (el.textContent && el.textContent.includes("Raw content")) {
              // Don't hide if inside markscheme preview
              if (!el.closest(".markscheme-preview-container")) {
                el.style.display = "none";
                processedElements.add(el);

                // Hide next element (likely the raw content)
                if (
                  el.nextElementSibling &&
                  !processedElements.has(el.nextElementSibling) &&
                  !el.nextElementSibling.closest(
                    ".markscheme-preview-container"
                  )
                ) {
                  el.nextElementSibling.style.display = "none";
                  processedElements.add(el.nextElementSibling);
                }
              }
            }
          });
      } finally {
        isProcessing = false;
      }
    }

    // Run immediately once
    hideRawContent();

    // Run again after a delay to catch elements that may be lazily loaded
    const timeoutId = setTimeout(hideRawContent, 1000);

    // Use MutationObserver with throttling to prevent infinite loops
    let pendingMutations = false;
    const observer = new MutationObserver(() => {
      if (!pendingMutations) {
        pendingMutations = true;
        setTimeout(() => {
          pendingMutations = false;
          hideRawContent();
        }, 500); // Throttle to prevent rapid consecutive updates
      }
    });

    // Observe only childList mutations (new elements added) to reduce loop risk
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      document.head.removeChild(styleEl);
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  return null; // This component doesn't render anything
};

const QuestionImageUpload = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedContent, setExtractedContent] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);

  // Add state for showing HTML source tabs
  const [showHtmlSourceTabs, setShowHtmlSourceTabs] = useState(false);
  // Add a flag to control whether the HTML Source buttons are visible
  const showHtmlSourceButtons = false; // Set this to false to hide the buttons

  // New state for markscheme functionality
  const [markschemeImage, setMarkschemeImage] = useState(null);
  const [markschemeImagePreview, setMarkschemeImagePreview] = useState(null);
  const [extractedMarkschemeContent, setExtractedMarkschemeContent] =
    useState(null);
  const [generatedMarkschemeContent, setGeneratedMarkschemeContent] =
    useState(null);
  const [markschemeSource, setMarkschemeSource] = useState("NONE"); // 'NONE', 'USER', 'AI'
  const [isGeneratingMarkscheme, setIsGeneratingMarkscheme] = useState(false);

  // New state for exam board selection
  const [showExamBoardModal, setShowExamBoardModal] = useState(false);
  const [selectedExamBoard, setSelectedExamBoard] = useState("");

  // Form data for question details
  const [questionData, setQuestionData] = useState({
    title: "",
    subject: "",
    level: "",
    marks: "",
    paper: "",
    question_number: "",
    reference_code: "",
    question_body: "",
    markscheme_body: "",
    is_private: true,
  });

  // Fetch subjects and levels on component mount
  useEffect(() => {
    fetchSubjects();
    fetchLevels();

    // Set initial reference code
    setQuestionData((prevData) => ({
      ...prevData,
      reference_code: generateRefCode(),
    }));
  }, []);

  const fetchSubjects = async () => {
    try {
      const response = await api.get("/api/csv-data/subjects/");
      if (Array.isArray(response.data)) {
        setSubjects(response.data);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchLevels = async () => {
    try {
      // First try to get from a dedicated levels endpoint
      try {
        const response = await api.get("/api/csv-data/levels/");
        if (Array.isArray(response.data)) {
          setLevels(response.data);
          return;
        }
      } catch (err) {
        console.log("Levels endpoint not available, extracting from data");
      }

      // Fallback: Extract levels from CSV data
      const response = await api.get("/api/csv-data/?limit=1000");
      if (response.data && Array.isArray(response.data.results)) {
        const uniqueLevels = new Set();
        response.data.results.forEach((item) => {
          if (item.level) {
            uniqueLevels.add(item.level);
          }
        });
        setLevels(Array.from(uniqueLevels));
        console.log("Extracted levels:", Array.from(uniqueLevels));
      }
    } catch (error) {
      console.error("Error fetching levels:", error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
      setExtractedContent(null);
      setError(null);
    }
  };

  // Handler for markscheme image change
  const handleMarkschemeImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMarkschemeImage(file);
      setMarkschemeImagePreview(URL.createObjectURL(file));
      setExtractedMarkschemeContent(null);
    }
  };

  const handleProcessImage = async () => {
    if (!image) {
      setError("Please select an image first");
      return;
    }

    setProcessing(true);
    setError(null);

    const formData = new FormData();
    formData.append("image", image);

    try {
      // Log what we're sending
      console.log(
        "Sending image to process:",
        image.name,
        image.type,
        image.size
      );

      const response = await api.post(
        "/api/process-question-image/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Full Mathpix API response:", response.data);

      if (response.data.success) {
        // Get HTML content, ensure it's a string
        let htmlContent = response.data.html_content || "";

        console.log("Received HTML content length:", htmlContent.length);
        console.log("HTML content preview:", htmlContent.substring(0, 100));

        // Ensure we're working with clean HTML
        if (htmlContent.includes("<mathml")) {
          console.log("HTML contains MathML - this is good for rendering");
        }

        // If no HTML content, try to extract from full_result
        if (!htmlContent || htmlContent.trim() === "") {
          console.log(
            "HTML content is empty, checking for alternative content"
          );

          // Try to extract content from full_result if provided
          const fullResult = response.data.full_result || {};

          if (fullResult.html) {
            console.log("Using 'html' field from the full result");
            htmlContent = fullResult.html;
          } else if (fullResult.text) {
            console.log("Using 'text' field from the full result");
            htmlContent = `<div>${fullResult.text}</div>`;
          } else {
            // If we still can't find content, show an error
            setError(
              "The image could not be processed. No text was extracted."
            );
            setProcessing(false);
            return;
          }
        }

        // Make sure we have paragraph tags for better rendering
        if (!htmlContent.includes("<div>") && !htmlContent.includes("<p>")) {
          htmlContent = `<div>${htmlContent}</div>`;
        }

        setExtractedContent(htmlContent);
        setQuestionData({
          ...questionData,
          question_body: htmlContent,
        });

        // Advance to the next step
        setActiveStep(1);
      } else {
        setError(
          response.data.error || "Failed to process image. Please try again."
        );
      }
    } catch (error) {
      console.error("Error processing image:", error);
      setError(
        error.response?.data?.error ||
          error.message ||
          "An error occurred while processing the image"
      );
    } finally {
      setProcessing(false);
    }
  };

  // Function to process the markscheme image
  const handleProcessMarkschemeImage = async () => {
    if (!markschemeImage) {
      setError("Please select a markscheme image first");
      return;
    }

    setProcessing(true);
    setError(null);

    const formData = new FormData();
    formData.append("image", markschemeImage);

    try {
      const response = await api.post(
        "/api/process-markscheme-image/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        // Get HTML content, ensure it's a string
        let htmlContent = response.data.html_content || "";

        // Make sure we have paragraph tags for better rendering
        if (!htmlContent.includes("<div>") && !htmlContent.includes("<p>")) {
          htmlContent = `<div>${htmlContent}</div>`;
        }

        setExtractedMarkschemeContent(htmlContent);
        setMarkschemeSource("USER");
        setQuestionData({
          ...questionData,
          markscheme_body: htmlContent,
        });
      } else {
        setError(
          response.data.error ||
            "Failed to process markscheme image. Please try again."
        );
      }
    } catch (error) {
      console.error("Error processing markscheme image:", error);
      setError(
        error.response?.data?.error ||
          error.message ||
          "An error occurred while processing the markscheme image"
      );
    } finally {
      setProcessing(false);
    }
  };

  // Function to generate markscheme with GPT-4o
  const handleGenerateMarkscheme = async () => {
    if (!extractedContent) {
      setError("Process a question image first before generating a markscheme");
      return;
    }

    // First show the exam board selection modal
    setShowExamBoardModal(true);
  };

  // Function to proceed with markscheme generation after exam board selection
  const handleProceedWithMarkschemeGeneration = async () => {
    setShowExamBoardModal(false);
    setIsGeneratingMarkscheme(true);
    setError(null);

    try {
      const response = await api.post("/api/generate-markscheme/", {
        question_html: extractedContent,
        exam_board: selectedExamBoard,
      });

      if (response.data.success) {
        const markschemeHtml = response.data.markscheme_html;
        setGeneratedMarkschemeContent(markschemeHtml);
        setMarkschemeSource("AI");

        // Update the question data with the markscheme
        const updatedQuestionData = {
          ...questionData,
          markscheme_body: markschemeHtml,
        };

        // If topics were returned, update the title field with them
        if (response.data.topics && response.data.topics.length > 0) {
          console.log("Topics received from API:", response.data.topics);
          updatedQuestionData.title = response.data.topics.join(", ");
          console.log(
            "Updated title field with topics:",
            updatedQuestionData.title
          );
        } else {
          console.log("No topics received from the API");

          // As a fallback, look for TOPICS: in the markscheme itself
          if (markschemeHtml && markschemeHtml.includes("TOPICS:")) {
            console.log(
              "Found TOPICS: in markscheme HTML, attempting to extract manually"
            );
            try {
              const topicsMatch = markschemeHtml.match(/TOPICS:\s*([^<\n]+)/i);
              if (topicsMatch && topicsMatch[1]) {
                const extractedTopics = topicsMatch[1]
                  .split(",")
                  .map((t) => t.trim())
                  .filter((t) => t);
                console.log("Manually extracted topics:", extractedTopics);
                if (extractedTopics.length > 0) {
                  updatedQuestionData.title = extractedTopics.join(", ");
                  console.log(
                    "Updated title field with manually extracted topics:",
                    updatedQuestionData.title
                  );
                }
              }
            } catch (err) {
              console.error("Error trying to manually extract topics:", err);
            }
          }
        }

        setQuestionData(updatedQuestionData);
      } else {
        setError(
          response.data.error ||
            "Failed to generate markscheme. Please try again."
        );
      }
    } catch (error) {
      console.error("Error generating markscheme:", error);
      setError(
        error.response?.data?.error ||
          error.message ||
          "An error occurred while generating the markscheme"
      );
    } finally {
      setIsGeneratingMarkscheme(false);
    }
  };

  // Function to select which markscheme to use (user uploaded or AI generated)
  const handleSelectMarkscheme = (source) => {
    if (source === "USER" && extractedMarkschemeContent) {
      setMarkschemeSource("USER");
      setQuestionData({
        ...questionData,
        markscheme_body: extractedMarkschemeContent,
      });
    } else if (source === "AI" && generatedMarkschemeContent) {
      setMarkschemeSource("AI");
      setQuestionData({
        ...questionData,
        markscheme_body: generatedMarkschemeContent,
      });
    } else if (source === "NONE") {
      setMarkschemeSource("NONE");
      setQuestionData({
        ...questionData,
        markscheme_body: "",
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setQuestionData({
      ...questionData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmitQuestion = async () => {
    if (!extractedContent) {
      setError("No question content to save");
      return;
    }

    if (!questionData.title || !questionData.subject) {
      setError("Title and subject are required");
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      console.log("Saving question with markscheme:", markschemeSource);
      const response = await api.post("/api/user-questions/", {
        ...questionData,
        question_body: extractedContent,
        markscheme_body: questionData.markscheme_body,
      });

      // Move to the final step
      setActiveStep(4);
    } catch (err) {
      console.error("Error saving question:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to save question. Please try again."
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setImage(null);
    setImagePreview(null);
    setExtractedContent(null);
    setError(null);
    setProcessing(false);

    // Reset markscheme state
    setMarkschemeImage(null);
    setMarkschemeImagePreview(null);
    setExtractedMarkschemeContent(null);
    setGeneratedMarkschemeContent(null);
    setMarkschemeSource("NONE");
    setIsGeneratingMarkscheme(false);

    setQuestionData({
      title: "",
      subject: "",
      level: "",
      marks: "",
      paper: "",
      question_number: "",
      reference_code: generateRefCode(),
      question_body: "",
      markscheme_body: "",
      is_private: true,
    });
  };

  // Generate a random reference code
  const generateRefCode = () => {
    const prefix = "QU";
    const randomDigits = Math.floor(Math.random() * 1000000)
      .toString()
      .padStart(6, "0");
    const timestamp = Date.now().toString().slice(-4);
    return `${prefix}${randomDigits}${timestamp}`;
  };

  return (
    <Container className="py-4">
      {/* Include the raw content hider component */}
      <RawContentHider />

      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h2 className="mb-0 fs-3 fw-bold" style={{ color: "black" }}>
            Upload Exam Question
          </h2>
        </Card.Header>
        <Card.Body>
          {/* Add steps for the upload process */}
          <ProgressBar
            now={(activeStep / 4) * 100}
            className="mb-4"
            variant={error ? "danger" : "primary"}
          />

          {error && <Alert variant="danger">{error}</Alert>}

          {/* Step content */}

          {/* Step 1: Image Upload */}
          {activeStep === 0 && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Select a question image</Form.Label>
                <Form.Control
                  type="file"
                  onChange={handleImageChange}
                  accept="image/*"
                  disabled={processing}
                />
                <Form.Text className="text-muted">
                  Supported formats: JPG, PNG, GIF, etc.
                </Form.Text>
              </Form.Group>

              {imagePreview && (
                <div className="mb-3">
                  <h6>Preview:</h6>
                  <Image
                    src={imagePreview}
                    alt="Preview"
                    style={{ maxWidth: "100%", maxHeight: "300px" }}
                    thumbnail
                  />
                </div>
              )}

              {/* Markscheme Image Upload (Optional) */}
              <hr className="my-4" />
              <h5>Upload Markscheme (Optional)</h5>
              <Form.Group className="mb-3">
                <Form.Label>Select a markscheme image</Form.Label>
                <Form.Control
                  type="file"
                  onChange={handleMarkschemeImageChange}
                  accept="image/*"
                  disabled={processing}
                />
                <Form.Text className="text-muted">
                  Optional: Upload an image containing the markscheme for this
                  question
                </Form.Text>
              </Form.Group>

              {markschemeImagePreview && (
                <div className="mb-3">
                  <h6>Markscheme Preview:</h6>
                  <Image
                    src={markschemeImagePreview}
                    alt="Markscheme Preview"
                    style={{ maxWidth: "100%", maxHeight: "300px" }}
                    thumbnail
                  />
                </div>
              )}

              {markschemeImage && (
                <Button
                  variant="outline-primary"
                  onClick={handleProcessMarkschemeImage}
                  disabled={processing || !markschemeImage}
                  className="mb-3 me-2"
                >
                  {processing ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Processing Markscheme...
                    </>
                  ) : (
                    "Process Markscheme Image"
                  )}
                </Button>
              )}

              <div className="mt-4">
                <Button
                  variant="primary"
                  onClick={handleProcessImage}
                  disabled={!image || processing}
                  className="me-2"
                >
                  {processing ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Processing Question...
                    </>
                  ) : (
                    "Process Question Image"
                  )}
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Question Details */}
          {activeStep === 1 && (
            <>
              <div className="mb-4">
                <h5>Extracted Question Content:</h5>
                {showHtmlSourceButtons && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div></div>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setShowHtmlSourceTabs(!showHtmlSourceTabs)}
                      className="text-muted"
                    >
                      {showHtmlSourceTabs
                        ? "Hide HTML Source"
                        : "Show HTML Source"}
                    </Button>
                  </div>
                )}
                <Tabs
                  defaultActiveKey="rendered"
                  id="content-tabs"
                  className="mb-3"
                >
                  <Tab eventKey="rendered" title="Rendered Content">
                    {extractedContent ? (
                      <MathJaxRenderer
                        content={extractedContent}
                        className="border p-3 bg-light rounded"
                      />
                    ) : (
                      <Alert variant="warning">
                        No content was extracted from the image. Please try
                        again with a different image.
                      </Alert>
                    )}
                  </Tab>
                  {showHtmlSourceTabs && (
                    <Tab eventKey="raw" title="HTML Source">
                      {extractedContent ? (
                        <pre
                          className="border p-3 bg-light rounded"
                          style={{ maxHeight: "300px", overflow: "auto" }}
                        >
                          {extractedContent}
                        </pre>
                      ) : (
                        <Alert variant="warning">No content available.</Alert>
                      )}
                    </Tab>
                  )}
                </Tabs>
                {extractedContent && extractedContent.length < 10 && (
                  <Alert variant="warning" className="mt-2">
                    Very little content was extracted. The image may not be
                    clear enough or might not contain recognizable text.
                  </Alert>
                )}
              </div>

              {/* Markscheme Section */}
              <div className="mb-4">
                <h5>Markscheme:</h5>

                {/* GPT-4o Markscheme Generation */}
                {!generatedMarkschemeContent && !isGeneratingMarkscheme && (
                  <Button
                    variant="success"
                    onClick={handleGenerateMarkscheme}
                    className="mb-3"
                  >
                    Generate Markscheme
                  </Button>
                )}

                {isGeneratingMarkscheme && (
                  <div className="d-flex align-items-center mb-3">
                    <Spinner
                      animation="border"
                      role="status"
                      size="sm"
                      className="me-2"
                    />
                    <span>Generating markscheme...</span>
                  </div>
                )}

                {/* AI-generated markscheme disclaimer */}
                {generatedMarkschemeContent && (
                  <Alert variant="info" className="mb-3">
                    <strong>Disclaimer:</strong> AI-generated markschemes may
                    not be 100% accurate. Please review and verify the content
                    before using it for assessment purposes.
                  </Alert>
                )}

                {/* Markscheme Selection Options */}
                {(extractedMarkschemeContent || generatedMarkschemeContent) && (
                  <div className="mb-3 border p-3 bg-light rounded">
                    <h6>Select Markscheme Source:</h6>
                    <Form.Check
                      type="radio"
                      name="markschemeSource"
                      id="uploadedMarkscheme"
                      label="Use uploaded markscheme"
                      checked={markschemeSource === "USER"}
                      onChange={() => handleSelectMarkscheme("USER")}
                      disabled={!extractedMarkschemeContent}
                      className="mb-2"
                    />
                    <Form.Check
                      type="radio"
                      name="markschemeSource"
                      id="generatedMarkscheme"
                      label="Use AI-generated markscheme"
                      checked={markschemeSource === "AI"}
                      onChange={() => handleSelectMarkscheme("AI")}
                      disabled={!generatedMarkschemeContent}
                      className="mb-2"
                    />
                    <Form.Check
                      type="radio"
                      name="markschemeSource"
                      id="noMarkscheme"
                      label="No markscheme"
                      checked={markschemeSource === "NONE"}
                      onChange={() => handleSelectMarkscheme("NONE")}
                    />
                  </div>
                )}

                {/* Display Selected Markscheme */}
                {markschemeSource !== "NONE" && (
                  <>
                    {showHtmlSourceButtons && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div></div>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() =>
                            setShowHtmlSourceTabs(!showHtmlSourceTabs)
                          }
                          className="text-muted"
                        >
                          {showHtmlSourceTabs
                            ? "Hide HTML Source"
                            : "Show HTML Source"}
                        </Button>
                      </div>
                    )}
                    <Tabs
                      defaultActiveKey="rendered"
                      id="markscheme-tabs"
                      className="mb-3 markscheme-preview-container"
                    >
                      <Tab
                        eventKey="rendered"
                        title="Rendered Markscheme"
                        className="markscheme-content"
                      >
                        <div className="markscheme-preview-container border p-3 bg-light rounded">
                          {markschemeSource === "USER" ? (
                            <MathJaxRenderer
                              content={extractedMarkschemeContent}
                              className="markscheme-content"
                            />
                          ) : markschemeSource === "AI" ? (
                            <MathJaxRenderer
                              content={generatedMarkschemeContent}
                              className="markscheme-content"
                            />
                          ) : (
                            <Alert variant="info">
                              No markscheme selected.
                            </Alert>
                          )}
                        </div>
                      </Tab>
                      {showHtmlSourceTabs && (
                        <Tab eventKey="raw" title="HTML Source">
                          {markschemeSource === "USER" ? (
                            <pre
                              className="border p-3 bg-light rounded"
                              style={{ maxHeight: "300px", overflow: "auto" }}
                            >
                              {extractedMarkschemeContent}
                            </pre>
                          ) : markschemeSource === "AI" ? (
                            <pre
                              className="border p-3 bg-light rounded"
                              style={{ maxHeight: "300px", overflow: "auto" }}
                            >
                              {generatedMarkschemeContent}
                            </pre>
                          ) : (
                            <Alert variant="info">
                              No markscheme selected.
                            </Alert>
                          )}
                        </Tab>
                      )}
                    </Tabs>
                  </>
                )}
              </div>

              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Related Topics</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={questionData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Subject</Form.Label>
                      <Form.Select
                        name="subject"
                        value={questionData.subject}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Subject</option>
                        {subjects.map((subject, index) => (
                          <option key={index} value={subject}>
                            {subject}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Level</Form.Label>
                      <Form.Select
                        name="level"
                        value={questionData.level}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Level</option>
                        {levels.map((level, index) => (
                          <option key={index} value={level}>
                            {level}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Marks</Form.Label>
                      <Form.Control
                        type="number"
                        name="marks"
                        value={questionData.marks}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="Number of marks"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Paper (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        name="paper"
                        value={questionData.paper}
                        onChange={handleInputChange}
                        placeholder="e.g., P1, P2, etc."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Question Number (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        name="question_number"
                        value={questionData.question_number}
                        onChange={handleInputChange}
                        placeholder="e.g., Q5, 7b, etc."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="is_private"
                    label="Make this question private (only visible to you)"
                    checked={questionData.is_private}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="secondary"
                    onClick={handleReset}
                    disabled={processing}
                  >
                    Start Over
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSubmitQuestion}
                    disabled={processing || !extractedContent}
                  >
                    {processing ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      "Save Question"
                    )}
                  </Button>
                </div>
              </Form>
            </>
          )}

          {/* Final step - Success message */}
          {activeStep === 4 && (
            <div className="text-center py-5">
              <Alert variant="success">
                <h4>Question Saved Successfully!</h4>
                <p>Your question has been added to your collection.</p>
              </Alert>
              <div className="mt-4">
                <Button
                  variant="primary"
                  onClick={handleReset}
                  className="me-2"
                >
                  Upload Another Question
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => navigate("/my-questions")}
                >
                  View My Questions
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Exam Board Selection Modal */}
      <Modal
        show={showExamBoardModal}
        onHide={() => setShowExamBoardModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Exam Board</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>
                Select Exam Board for Topic Classification
              </Form.Label>
              <Form.Select
                value={selectedExamBoard}
                onChange={(e) => setSelectedExamBoard(e.target.value)}
              >
                <option value="">Select an Exam Board</option>
                <option value="P1">Singapore Primary 1</option>
                <option value="P2">Singapore Primary 2</option>
                <option value="P3">Singapore Primary 3</option>
                <option value="P4">Singapore Primary 4</option>
                <option value="P5">Singapore Primary 5</option>
                <option value="PSLE">Singapore PSLE</option>
                <option value="LS">Singapore Lower Secondary</option>
                <option value="OL">Singapore GCE O-Levels</option>
                <option value="AL">Singapore GCE A-Levels</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowExamBoardModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleProceedWithMarkschemeGeneration}
          >
            Proceed
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default QuestionImageUpload;
